import { cx } from "@emotion/css";
import {
	Clock,
	Heart,
	type LucideIcon,
	ThumbsDown,
	ThumbsUp,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { TextButton } from "src/components/Default/LekUI/Button";
import type { AnimateExitFn, ExitColor, ExitDirection } from "./Flashcard";

interface Variant {
	grade: number;
	interval?: string;
}

interface FlashcardControllerProps {
	variants: Variant[];
	animateExit: AnimateExitFn;
	resolveFlashcard: (grade: number) => void;
}

const gradeMeta: Record<number, { className: string; Icon: LucideIcon }> = {
	0: { className: "error-text-btn", Icon: Clock },
	1: { className: "warning-text-btn", Icon: ThumbsDown },
	2: { className: "success-text-btn", Icon: ThumbsUp },
	3: { className: "primary-important-text-btn", Icon: Heart },
};

export const gradeAnimationByGrade: Record<
	number,
	{ key: string; direction: ExitDirection; color: ExitColor }
> = {
	0: { key: "ArrowLeft", direction: "left", color: "red" },
	1: { key: "ArrowDown", direction: "down", color: "orange" },
	2: { key: "ArrowUp", direction: "up", color: "green" },
	3: { key: "ArrowRight", direction: "right", color: "blue" },
};

const INTERVAL_REGEX = /(\d+)\s*(minute|hour|day|month|year)s?/;

export function FlashcardController({
	variants,
	animateExit,
	resolveFlashcard,
}: FlashcardControllerProps) {
	const { t } = useTranslation();

	// ponytail: backend zwraca np. "6 minutes" / "4 days"; nieznany format pokazujemy surowo
	const formatInterval = (interval: string, short = false) => {
		const match = interval.match(INTERVAL_REGEX);
		if (!match) return interval;
		const key = short ? "intervalShort" : "interval";
		return t(`decks.${key}.${match[2]}`, { count: Number(match[1]) });
	};

	return (
		<div className="flashcard-app--controller">
			{variants.map((variant) => {
				const meta = gradeMeta[variant.grade];
				const animation = gradeAnimationByGrade[variant.grade];
				if (!meta || !animation || !variant.interval) {
					return null;
				}

				return (
					<TextButton
						key={variant.grade}
						onClick={() =>
							animateExit(animation.direction, animation.color, () =>
								resolveFlashcard(variant.grade),
							)
						}
						className={cx(meta.className, "flashcard-app--option")}
						data-test-id={`flashcard-grade-button-${variant.grade}`}
					>
						<span className="flashcard-app--option-label">
							<meta.Icon className="icon-super-small" />
							{t(`decks.gradeButtons.${variant.grade}`)}
						</span>
						<span className="flashcard-app--option-interval">
							{formatInterval(variant.interval)}
						</span>
						<span className="flashcard-app--option-interval-short">
							{formatInterval(variant.interval, true)}
						</span>
					</TextButton>
				);
			})}
		</div>
	);
}
